/**
 * Browser client for the Supabase session store. This is one leg of the
 * dual-store facade in sessionAPIs.ts, which decides whether Supabase or Rails
 * is primary for the page and handles fallback between them; activity code
 * should call SessionApi there, not this class directly.
 *
 * Access: the browser never touches the table directly (RLS denies the anon
 * role). Reads and writes go through two SECURITY DEFINER RPCs that each act
 * on one row by uid. See app/services/supabase/README.md.
 *
 * Retries: transient failures (network errors, Cloudflare/Supabase 5xx, 429)
 * are retried up to MAX_ATTEMPTS times with exponential backoff and jitter,
 * capped at MAX_DELAY_MS. After the last attempt the error is reported to
 * Sentry and the promise rejects. Plain 4xx errors are not retried.
 *
 * Supersession: saves fire as a student answers each question, and a retry of
 * an older save must never land after a newer one. So each session uid has at
 * most one "current" save, tracked in `inFlightUpdates`:
 *
 *   1. update(uid) aborts the previous AbortController for that uid, then
 *      registers a fresh one and attaches its signal to the request.
 *   2. Aborting tears down the older request if it is still in flight, and
 *      withRetry checks the signal before every attempt, so a retry that was
 *      waiting in backoff is never sent.
 *   3. The older save rejects with SessionServiceSuperseded, which is not
 *      reported: nothing was lost, because every caller sends the same set of
 *      top-level keys on each save and the server shallow-merges them, so the
 *      newer save carries everything the older one did.
 *
 * Not covered: two saves that both fully succeed but reach Postgres out of
 * order. Only a server-side version check could close that; with saves seconds
 * apart it is theoretical.
 */
import { PostgrestClient } from '@supabase/postgrest-js';
import * as Sentry from '@sentry/browser';

const GET_RPC = 'get_session_data';
const UPSERT_RPC = 'upsert_session_data';

const MAX_ATTEMPTS = 6;
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 4000;

let client: PostgrestClient | null = null;

function getClient(): PostgrestClient | null {
  if (client) return client;

  const env = (typeof window !== 'undefined' && window.postbuildEnv) || ({} as any);
  if (!env.SUPABASE_SERVICE_ENABLED) return null;

  const url = env.SUPABASE_URL;
  const key = env.SUPABASE_ANON_KEY;
  if (!url || !key) return null;

  client = new PostgrestClient(`${url}/rest/v1`, {
    headers: {
      apikey: key,
      Authorization: `Bearer ${key}`,
    },
  });
  return client;
}

interface PostgrestResult {
  data?: any;
  error?: { message?: string; code?: string; details?: string } | null;
  status?: number;
}

class SessionServiceError extends Error {
  status?: number;
  code?: string;
  transient: boolean;

  constructor(message: string, { status, code, transient }: { status?: number; code?: string; transient: boolean }) {
    super(message);
    this.name = 'SessionServiceError';
    // Required for `instanceof` to work when TS targets ES5.
    Object.setPrototypeOf(this, SessionServiceError.prototype);
    this.status = status;
    this.code = code;
    this.transient = transient;
  }
}

function isTransientStatus(status?: number): boolean {
  // 408 request timeout, 429 rate limited, and any 5xx (including Cloudflare's
  // 52x/555 origin errors) are worth retrying. 4xx otherwise means our
  // request is wrong and retrying won't help.
  if (!status) return true; // no status => network failure / aborted
  return status === 408 || status === 429 || status >= 500;
}

function toError(result: PostgrestResult): SessionServiceError | null {
  if (!result.error) return null;
  const status = result.status;
  return new SessionServiceError(result.error.message || 'Session service request failed', {
    status,
    code: result.error.code,
    transient: isTransientStatus(status),
  });
}

function backoffDelay(attempt: number): number {
  const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (attempt - 1));
  // Full jitter: uniform in [exp/2, exp]
  return Math.round(exp / 2 + Math.random() * (exp / 2));
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Most recent in-flight update per session uid. Starting a new update aborts
// the previous one for the same uid.
const inFlightUpdates = new Map<string, AbortController>();

function isAbortError(e: unknown): boolean {
  return e instanceof Error && e.name === 'AbortError';
}

// Thrown when a request is abandoned because a newer update for the same
// session superseded it. Not an error condition — the newer payload contains
// everything this one did — so it is never reported to Sentry.
class SessionServiceSuperseded extends Error {
  constructor() {
    super('Superseded by a newer update for this session');
    this.name = 'SessionServiceSuperseded';
    Object.setPrototypeOf(this, SessionServiceSuperseded.prototype);
  }
}

// Runs `fn`, retrying on transient failures with exponential backoff.
// Resolves with the PostgREST `data`, or rejects with the last error.
// If `signal` is aborted, stops immediately and rejects with
// SessionServiceSuperseded without reporting.
async function withRetry<T>(
  fn: (signal?: AbortSignal) => PromiseLike<PostgrestResult>,
  label: string,
  signal?: AbortSignal
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (signal?.aborted) throw new SessionServiceSuperseded();

    try {
      const result = await fn(signal);
      const error = toError(result);
      if (!error) return result.data as T;
      lastError = error;
      if (!error.transient) break;
    } catch (e) {
      if (signal?.aborted || isAbortError(e)) throw new SessionServiceSuperseded();
      // Thrown (rather than returned) errors from postgrest-js are fetch
      // failures: offline, DNS, connection reset, etc. Always retry.
      lastError = e instanceof Error ? e : new Error(String(e));
    }

    if (attempt < MAX_ATTEMPTS) {
      await sleep(backoffDelay(attempt));
    }
  }

  if (signal?.aborted) throw new SessionServiceSuperseded();

  const err = lastError || new Error(`${label} failed`);
  Sentry.captureException(err, {
    tags: { service: 'session-service', operation: label },
    extra: { attempts: MAX_ATTEMPTS },
  });
  throw err;
}

class SessionServiceApi {
  // Whether the Supabase client can actually issue requests (service enabled
  // and URL/key present). When this is false, get/update resolve as no-ops —
  // callers that need a real write (the SessionApi facade) must check this
  // rather than reading a resolved no-op as success.
  static available(): boolean {
    return getClient() !== null;
  }

  static get(uid: string): Promise<object | null> {
    const postgrest = getClient();
    if (!postgrest || !uid) return Promise.resolve(null);

    return withRetry<object | null>(
      () => postgrest.rpc(GET_RPC, { p_uid: uid }),
      'get'
    ).then((data) => data ?? null);
  }

  static update(uid: string, data: object): Promise<void> {
    const postgrest = getClient();
    // Some callers run without a session (e.g. the teacher-projected Lessons
    // view has no ?student= param). There is nothing to save for them, and the
    // RPC rejects an empty uid, so skip quietly.
    if (!postgrest || !uid) return Promise.resolve();

    // Supersede any earlier update for this session that is still retrying
    // or in flight; this payload is the newer, complete state.
    inFlightUpdates.get(uid)?.abort();
    const controller = new AbortController();
    inFlightUpdates.set(uid, controller);

    const done = () => {
      if (inFlightUpdates.get(uid) === controller) inFlightUpdates.delete(uid);
    };

    return withRetry<unknown>(
      (signal) => postgrest.rpc(UPSERT_RPC, { p_uid: uid, p_data: data }).abortSignal(signal!),
      'update',
      controller.signal
    ).then(
      () => { done(); },
      (e) => { done(); throw e; }
    );
  }
}

export { SessionServiceApi, SessionServiceError, SessionServiceSuperseded, MAX_ATTEMPTS, MAX_DELAY_MS };
